import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, CalendarClock, Search, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { CnapSearch } from "@/components/site/CnapSearch";
import { GreenCard } from "@/components/site/GreenCard";
import { ArticleCard } from "@/components/site/ArticleCard";

const SPECIMEN = { nom: "NOM", postnom: "POST-NOM", prenom: "Prénom", cnap: "00000000", date_delivrance: "2025-01-15", date_expiration: "2028-01-15", statut: "valide" };

const steps = [
  { icon: Search, t: "Saisissez le n° CNAP ou le nom", d: "Le numéro d'ordre à 8 chiffres figurant sur la carte verte, ou le nom de l'assistant en pharmacie." },
  { icon: ShieldCheck, t: "Confirmez l'inscription", d: "Le registre indique si l'AP est bien inscrit à l'UAPHARCO." },
  { icon: CalendarClock, t: "Contrôlez la validité", d: "La carte verte est valable 3 ans à compter de sa date de délivrance." },
];

const Hero = () => (
  <section className="relative overflow-hidden grain">
    <img src="/hero-pharmacy.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.12]" />
    <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white/80 to-transparent" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-28 grid lg:grid-cols-[1.15fr_1fr] gap-14 items-center">
      <div className="space-y-7 min-w-0">
        <span className="rise inline-flex items-center gap-2 rounded-full border border-emerald-700/20 bg-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-emerald-800">
          <ShieldCheck className="h-3.5 w-3.5" /> Registre officiel
        </span>
        <h1 className="rise d1 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
          Vérifiez un <span className="text-emerald-700">assistant en pharmacie</span> en quelques secondes.
        </h1>
        <p className="rise d2 text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
          Entrez le numéro CNAP ou le nom pour confirmer l'inscription d'un AP auprès de l'UAPHARCO et la validité de sa carte verte professionnelle.
        </p>
        <div className="rise d3 max-w-xl"><CnapSearch large /></div>
      </div>
      <div className="rise d4 flex justify-center lg:justify-end">
        <div className="w-full max-w-[460px] space-y-3">
          <GreenCard ap={SPECIMEN} demo />
          <p className="text-xs text-slate-500 pl-1">Spécimen de carte verte — validité de 3 ans.</p>
        </div>
      </div>
    </div>
  </section>
);

const Steps = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
    <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 max-w-lg">Comment fonctionne la vérification</h2>
    <div className="mt-10 grid md:grid-cols-3 gap-6">
      {steps.map((s, i) => (
        <div key={s.t} className="rounded-2xl border border-emerald-900/10 bg-white p-7 hover:border-emerald-700/30 transition-colors">
          <div className="flex items-center justify-between">
            <s.icon className="h-6 w-6 text-emerald-700" />
            <span className="font-mono text-sm text-slate-300">0{i + 1}</span>
          </div>
          <h3 className="mt-6 font-display text-lg font-semibold text-slate-900">{s.t}</h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
        </div>
      ))}
    </div>
  </section>
);

export default function Home() {
  const [articles, setArticles] = useState([]);
  useEffect(() => { api.get("/articles").then((r) => setArticles(r.data.slice(0, 3))).catch(() => {}); }, []);
  return (
    <>
      <Hero />
      <Steps />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4">
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Derniers communiqués</h2>
          <Link to="/communiques" data-testid="home-all-news-link" className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-800 hover:gap-2 transition-[gap]">Tout voir <ArrowRight className="h-4 w-4" /></Link>
        </div>
        {articles.length ? (
          <div className="mt-8 grid md:grid-cols-3 gap-6">{articles.map((a) => <ArticleCard key={a.id} a={a} />)}</div>
        ) : (
          <p data-testid="home-no-news" className="mt-8 text-slate-500">Aucun communiqué publié pour le moment.</p>
        )}
      </section>
    </>
  );
}
